import React, { useCallback, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, IconButton, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/ModeEdit";
import PropTypes from "prop-types";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PreviewIcon from "@mui/icons-material/Preview";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import Tooltip from "Components/Common/Tooltip";
import { POWER_CGRF_ADMIN, SUPER_ADMIN } from "common/constant";
import { useSelector } from "react-redux";
import ComplaintModal from "./ComplaintModal";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import moment from "moment";
import FilterApplicationRequests from "./Common/FilterApplicationRequests";
import { CGRF } from "tagTypes";
import { CONSUMER } from "common/constant";
import { getOptionLabel, getOptionValue } from "common/enums";
import { useSearchParams } from "react-router-dom";
import CGRFApplicationViewModal from "./CGRFApplicationViewModal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import { usePutCloseGrievance } from "apis/CGRFGiftPCL";
import { getImageUrl } from "utils";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";

const RECORDS_PER_PAGE = 10;

const CGRFGiftPCL = ({ t }) => {
  const [closeRequest, { isLoading: isClosing }] = usePutCloseGrievance();
  const [applicationId, setApplicationId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState({});
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const { roles } = useSelector((state) => state.Login);
  const [closingRequestId, setClosingRequestId] = useState(null);
  const [documentToPreview, setDocumentToPreview] = useState(null);

  const isSuperAdmin = roles.includes(SUPER_ADMIN);

  const isConsumer = useMemo(() => roles.includes(CONSUMER), [roles]);
  const isCloseAllowed = useMemo(
    () => roles.includes(POWER_CGRF_ADMIN) || roles.includes(SUPER_ADMIN),
    [roles]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const viewApplicationId = searchParams.get("applicationId");

  const lg = useMediaQuery("(min-width: 768px) and (max-width: 1399px)");
  const md = useMediaQuery("(min-width: 576px) and (max-width: 767px)");
  const sm = useMediaQuery("(max-width: 575px)");

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const viewApplicationBtnHandler = (applicationId) => {
    searchParams.set("applicationId", applicationId);
    setSearchParams(searchParams);
  };

  const columns = useMemo(
    () => [
      { title: "Consumer Number", field: "consumerNumber" },
      { title: "Name", field: "consumerName" },
      { title: "Address", field: "consumerAddress" },
      { title: "Nature of Connection", field: "natureOfConnection" },
      {
        title: "Submission Date",
        renderCell: ({ submissionDate }) =>
          moment(submissionDate).isValid()
            ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Grievance Category",
        field: "grievanceCategories",
      },
      {
        title: "Status",
        renderCell: ({ status }) => getOptionLabel("cgrfStatus", status) ?? "-",
      },
      {
        title: "Actions",
        renderCell: ({ id, status, url }) => {
          const isStatusSubmitted =
            status === getOptionValue("cgrfStatus", "Submitted");

          return (
            <Box sx={{ minWidth: "110px" }}>
              {/* View icon for application */}
              <IconButton onClick={() => viewApplicationBtnHandler(id)}>
                <Tooltip title={t("View Application Details")}>
                  <RemoveRedEyeIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
              <Tooltip title={t("Edit Application Form")}>
                {isConsumer && (
                  <IconButton
                    disabled={isStatusSubmitted}
                    onClick={() => {
                      setApplicationId(id);
                      setIsOpen(true);
                    }}
                  >
                    <EditIcon
                      color={!isStatusSubmitted ? "primary" : "disabled"}
                      fontSize="small"
                    />
                  </IconButton>
                )}
              </Tooltip>
              {isStatusSubmitted && isCloseAllowed && (
                <IconButton onClick={() => setClosingRequestId(id)}>
                  <Tooltip title={t("Close Request")}>
                    <CheckCircleIcon
                      fontSize="small"
                      className="cursor-pointer"
                      color="success"
                    />
                  </Tooltip>
                </IconButton>
              )}
              {!!url && (
                <Tooltip title={t("Grievance Form")}>
                  <IconButton
                    onClick={() => {
                      setDocumentToPreview({
                        url: getImageUrl(url),
                        documentTypeName: "Grievance",
                        documentType: "pdf",
                        documentName: "Grievance",
                      });
                    }}
                    size="small"
                  >
                    <PreviewIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    []
  );

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "grievance",
      tags: [CGRF],
      query,
    }),
    [query]
  );

  const onConfirm = async (id) => {
    try {
      await closeRequest(id);
      setClosingRequestId(null);
    } catch (err) {}
  };

  return (
    <div className="page-content h-100">
      {!!viewApplicationId && (
        <CGRFApplicationViewModal
          applicationId={viewApplicationId}
          handleClose={() => {
            searchParams.delete("applicationId");
            setSearchParams(searchParams);
          }}
        />
      )}
      {!!closingRequestId && (
        <ConfirmationDialog
          message="Are you sure you want to close this request?"
          onCancel={() => setClosingRequestId(false)}
          onConfirm={() => onConfirm(closingRequestId)}
          isOpen={!!closingRequestId}
          title="Warning"
          variant="warning"
          isLoading={isClosing}
        />
      )}
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => {
            setDocumentToPreview(null);
          }}
          {...documentToPreview}
        />
      )}

      {isOpen && (
        <ComplaintModal
          handleClose={() => {
            setIsOpen(false);
            setApplicationId("");
          }}
          isOpen={isOpen}
          applicationId={applicationId}
          isSuperAdmin={isSuperAdmin}
        />
      )}
      <BreadCrumb title={t("Complaint Form")} pageTitle={t("CGRF")} />

      <FilterApplicationRequests
        setQuery={setQuery}
        resetPage={handleResetPage}
      />

      {isConsumer && (
        <div className="text-end mb-2">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsOpen(true)}
          >
            {t("Add Complaint")}
          </Button>
        </div>
      )}

      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 400px)"
            : md
            ? "calc(100vh - 360px)"
            : lg
            ? "calc(100vh - 320px)"
            : "calc(100vh - 270px)"
        }
      />
    </div>
  );
};

CGRFGiftPCL.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(CGRFGiftPCL);
